import type { ITaxPort } from '@onboarding/application/ports';
import type { TaxConfig } from '@onboarding/domain/value-objects';
import { getTaxPercentValue } from '@onboarding/domain/value-objects';
import type { OnboardingHttpClientType } from '@onboarding/infrastructure/api';

/**
 * Concrete implementation of ITaxPort.
 *
 * Resolves the IVA tax UUID by percent value against the store's current tax list.
 * The backend guarantees that every new store is created with the IVA group
 * pre-populated with 16%, 8%, and 0% entries, so no create fallback is needed.
 *
 * Flow:
 *   1. GET /v1/taxes/?store_uuid={uuid}
 *   2. Flatten the IVA group and find the item matching the requested percent
 *   3. PUT /v1/stores/{uuid}/config/taxes/ { default_tax_id }
 */
export const createTaxRepository = (props: { httpClient: OnboardingHttpClientType }): ITaxPort => {
  const { httpClient } = props;

  const applyTaxConfig = async (config: TaxConfig): Promise<void> => {
    const targetValue = getTaxPercentValue(config.rate);
    const groups = await httpClient.getTaxes();

    const ivaGroup = groups.find((group) => group.name === 'IVA');
    const target = ivaGroup?.items.find((item) => item.type === 'PERCENTAGE' && item.value === targetValue);

    if (!target) {
      throw new Error(`IVA ${targetValue}% not found in store taxes`);
    }

    await httpClient.setDefaultTax({ default_tax_id: target.uuid });
  };

  return { applyTaxConfig };
};
